import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  XMarkIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { Institucion, InstitucionCrear, InstitucionActualizar } from '../../../services/institucionesService'

interface ModalInstitucionProps {
  isOpen: boolean
  onClose: () => void
  tipo: 'ver' | 'editar' | 'crear' | 'eliminar'
  institucion?: Institucion | null
  onGuardar: (data: InstitucionCrear | InstitucionActualizar) => Promise<void>
  onEliminar: () => Promise<void>
}

const ModalInstitucion: React.FC<ModalInstitucionProps> = ({
  isOpen,
  onClose,
  tipo,
  institucion,
  onGuardar,
  onEliminar
}) => {
  const [formData, setFormData] = useState<Partial<Institucion>>({
    nombre: '',
    descripcion: '',
    tipo: 'universidad',
    contacto_email: '',
    contacto_telefono: '',
    activo: true
  })
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [cargando, setCargando] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [pasoWizard, setPasoWizard] = useState(1)
  const [adminInicial, setAdminInicial] = useState({
    nombre: '',
    apellido: '',
    correo: '',
    telefono: '',
    cargo: '',
    password: ''
  })

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  useEffect(() => {
    if (institucion && (tipo === 'ver' || tipo === 'editar' || tipo === 'eliminar')) {
      setFormData(institucion)
    } else if (tipo === 'crear') {
      setFormData({
        nombre: '',
        descripcion: '',
        tipo: 'universidad',
        contacto_email: '',
        contacto_telefono: '',
        activo: true
      })
      setAdminInicial({
        nombre: '',
        apellido: '',
        correo: '',
        telefono: '',
        cargo: '',
        password: ''
      })
      setPasoWizard(1)
    }
    setErrores({})
  }, [institucion, tipo, isOpen])

  const validarFormulario = (): boolean => {
    const nuevosErrores: Record<string, string> = {}
    const esEmpresaNueva = tipo === 'crear' && formData.tipo === 'empresa'

    if (!formData.nombre?.trim()) {
      nuevosErrores.nombre = 'El nombre es requerido'
    }

    if (formData.contacto_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contacto_email)) {
      nuevosErrores.contacto_email = 'El email no es válido'
    }

    if (esEmpresaNueva && pasoWizard === 2) {
      if (!adminInicial.nombre.trim()) {
        nuevosErrores.admin_nombre = 'El nombre del administrador es requerido'
      }
      if (!adminInicial.correo.trim()) {
        nuevosErrores.admin_correo = 'El correo del administrador es requerido'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminInicial.correo)) {
        nuevosErrores.admin_correo = 'El correo del administrador no es válido'
      }
      if (!adminInicial.password.trim()) {
        nuevosErrores.admin_password = 'La contraseña inicial es requerida'
      } else if (adminInicial.password.trim().length < 8) {
        nuevosErrores.admin_password = 'La contraseña debe tener al menos 8 caracteres'
      }
    }

    setErrores(nuevosErrores)
    return Object.keys(nuevosErrores).length === 0
  }

  const manejarGuardar = async (e: React.FormEvent) => {
    e.preventDefault()
    const esEmpresaNueva = tipo === 'crear' && formData.tipo === 'empresa'

    if (esEmpresaNueva && pasoWizard === 1) {
      if (!validarFormulario()) return
      setPasoWizard(2)
      return
    }

    if (!validarFormulario()) return

    setCargando(true)
    try {
      const payload = esEmpresaNueva
        ? {
            ...formData,
            admin_inicial: {
              nombre: adminInicial.nombre.trim(),
              apellido: adminInicial.apellido.trim() || undefined,
              correo: adminInicial.correo.trim(),
              telefono: adminInicial.telefono.trim() || undefined,
              cargo: adminInicial.cargo.trim() || undefined,
              password: adminInicial.password,
            }
          }
        : formData

      await onGuardar(payload)
      onClose()
    } catch (error) {
      console.error('Error al guardar:', error)
    } finally {
      setCargando(false)
    }
  }

  const manejarEliminar = async () => {
    if (!institucion?.id && !institucion?.id_constitucion && !institucion?.id_organizacion) return
    setCargando(true)
    try {
      await onEliminar()
      onClose()
    } catch (error) {
      console.error('Error al eliminar:', error)
    } finally {
      setCargando(false)
    }
  }

  const manejarCambio = (campo: keyof Institucion, valor: any) => {
    setFormData(prev => ({ ...prev, [campo]: valor }))
    if (errores[campo]) {
      setErrores(prev => ({ ...prev, [campo]: '' }))
    }
  }

  if (!isOpen || !mounted) return null

  const esLectura = tipo === 'ver' || tipo === 'eliminar'
  const etiquetaEntidad = institucion?.origen === 'organizacion' || formData.tipo === 'empresa' ? 'Empresa' : 'Institución'
  const esWizardEmpresa = tipo === 'crear' && formData.tipo === 'empresa'

  return createPortal(
    <AnimatePresence>
      <div className="relative z-[9999]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all dark:bg-gray-800"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  {tipo === 'crear' && <BuildingOfficeIcon className="h-5 w-5 text-blue-500" />}
                  {tipo === 'editar' && <BuildingOfficeIcon className="h-5 w-5 text-yellow-500" />}
                  {tipo === 'eliminar' && <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />}
                  {tipo === 'ver' && <BuildingOfficeIcon className="h-5 w-5 text-gray-500" />}
                  {tipo === 'crear' ? `Nueva ${etiquetaEntidad}` : 
                   tipo === 'editar' ? `Editar ${etiquetaEntidad}` :
                   tipo === 'eliminar' ? `Eliminar ${etiquetaEntidad}` : `Detalles de la ${etiquetaEntidad}`}
                </h3>
                <button
                  onClick={onClose}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6">
            {tipo === 'eliminar' ? (
              <div className="text-center py-6">
                <ExclamationTriangleIcon className="h-16 w-16 text-red-100 text-red-500 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {`¿Estás seguro de eliminar esta ${etiquetaEntidad.toLowerCase()}?`}
                </h4>
                <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                  Esta acción no se puede deshacer. Se eliminarán todos los datos asociados.
                </p>
                <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800">
                  <p className="font-medium text-red-800 dark:text-red-200">
                    {formData.nombre}
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={manejarGuardar} className="space-y-6">
                {esWizardEmpresa && (
                  <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 dark:border-blue-900/40 dark:bg-blue-900/20">
                    <div className="flex items-center justify-between text-sm font-medium">
                      <span className={pasoWizard === 1 ? 'text-blue-700 dark:text-blue-200' : 'text-gray-500 dark:text-gray-400'}>1. Datos de la empresa</span>
                      <span className={pasoWizard === 2 ? 'text-blue-700 dark:text-blue-200' : 'text-gray-500 dark:text-gray-400'}>2. Usuario inicial</span>
                    </div>
                  </div>
                )}

                {(!esWizardEmpresa || pasoWizard === 1) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Nombre */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {`Nombre de la ${etiquetaEntidad}`}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={formData.nombre}
                        onChange={(e) => manejarCambio('nombre', e.target.value)}
                        disabled={esLectura}
                        className={`w-full pl-10 pr-4 py-2 border ${errores.nombre ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                        placeholder="Ej. Universidad de Colima"
                      />
                    </div>
                    {errores.nombre && <p className="mt-1 text-sm text-red-500">{errores.nombre}</p>}
                  </div>

                  {/* Tipo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tipo
                    </label>
                    <select
                      value={formData.tipo}
                      onChange={(e) => manejarCambio('tipo', e.target.value)}
                      disabled={esLectura}
                      className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                    >
                      <option value="universidad">Universidad</option>
                      <option value="instituto">Instituto</option>
                      <option value="escuela">Escuela</option>
                      <option value="empresa">Empresa</option>
                      <option value="gobierno">Gobierno</option>
                      <option value="agencia_aduanal">Agencia Aduanal</option>
                      <option value="transportista">Transportista</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>

                  {/* Estado */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Estado
                    </label>
                    <select
                      value={formData.activo ? 'activo' : 'inactivo'}
                      onChange={(e) => manejarCambio('activo', e.target.value === 'activo')}
                      disabled={esLectura}
                      className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email de Contacto
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        value={formData.contacto_email || ''}
                        onChange={(e) => manejarCambio('contacto_email', e.target.value)}
                        disabled={esLectura}
                        className={`w-full pl-10 pr-4 py-2 border ${errores.contacto_email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                        placeholder="contacto@institucion.com"
                      />
                    </div>
                    {errores.contacto_email && <p className="mt-1 text-sm text-red-500">{errores.contacto_email}</p>}
                  </div>

                  {/* Teléfono */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Teléfono
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <PhoneIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={formData.contacto_telefono || ''}
                        onChange={(e) => manejarCambio('contacto_telefono', e.target.value)}
                        disabled={esLectura}
                        className={`w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                        placeholder="(123) 456-7890"
                      />
                    </div>
                  </div>

                  {/* Descripción */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Descripción
                    </label>
                    <div className="relative">
                      <textarea
                        rows={3}
                        value={formData.descripcion || ''}
                        onChange={(e) => manejarCambio('descripcion', e.target.value)}
                        disabled={esLectura}
                        className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                        placeholder="Breve descripción de la institución..."
                      />
                    </div>
                  </div>
                </div>
                )}

                {esWizardEmpresa && pasoWizard === 2 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre del administrador
                      </label>
                      <input
                        type="text"
                        value={adminInicial.nombre}
                        onChange={(e) => setAdminInicial(prev => ({ ...prev, nombre: e.target.value }))}
                        className={`w-full px-4 py-2 border ${errores.admin_nombre ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg dark:bg-gray-700 dark:text-white`}
                        placeholder="Nombre"
                      />
                      {errores.admin_nombre && <p className="mt-1 text-sm text-red-500">{errores.admin_nombre}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Apellido
                      </label>
                      <input
                        type="text"
                        value={adminInicial.apellido}
                        onChange={(e) => setAdminInicial(prev => ({ ...prev, apellido: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="Apellido"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Correo del administrador
                      </label>
                      <input
                        type="email"
                        value={adminInicial.correo}
                        onChange={(e) => setAdminInicial(prev => ({ ...prev, correo: e.target.value }))}
                        className={`w-full px-4 py-2 border ${errores.admin_correo ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg dark:bg-gray-700 dark:text-white`}
                        placeholder="admin@empresa.com"
                      />
                      {errores.admin_correo && <p className="mt-1 text-sm text-red-500">{errores.admin_correo}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Teléfono
                      </label>
                      <input
                        type="text"
                        value={adminInicial.telefono}
                        onChange={(e) => setAdminInicial(prev => ({ ...prev, telefono: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="5551234567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cargo
                      </label>
                      <input
                        type="text"
                        value={adminInicial.cargo}
                        onChange={(e) => setAdminInicial(prev => ({ ...prev, cargo: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        placeholder="Director de Operaciones"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Contraseña inicial
                      </label>
                      <input
                        type="password"
                        value={adminInicial.password}
                        onChange={(e) => setAdminInicial(prev => ({ ...prev, password: e.target.value }))}
                        className={`w-full px-4 py-2 border ${errores.admin_password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg dark:bg-gray-700 dark:text-white`}
                        placeholder="Minimo 8 caracteres"
                      />
                      {errores.admin_password && <p className="mt-1 text-sm text-red-500">{errores.admin_password}</p>}
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={onClose}
              disabled={cargando}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Cancelar
            </button>
            {esWizardEmpresa && pasoWizard === 2 && !esLectura && (
              <button
                onClick={() => setPasoWizard(1)}
                disabled={cargando}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Anterior
              </button>
            )}
            {!esLectura && (
              <button
                onClick={manejarGuardar}
                disabled={cargando}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2"
              >
                {cargando ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5" />
                    <span>{esWizardEmpresa && pasoWizard === 1 ? 'Siguiente' : 'Guardar'}</span>
                  </>
                )}
              </button>
            )}
            {tipo === 'eliminar' && (
              <button
                onClick={manejarEliminar}
                disabled={cargando}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 flex items-center gap-2"
              >
                {cargando ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Eliminando...</span>
                  </>
                ) : (
                  <>
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <span>Eliminar</span>
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
    </div>
    </AnimatePresence>,
    document.body
  )
}

export default ModalInstitucion
